/* Hak bildirimini türeve geri yazar (plan §5.2 adım 4).

   Tuval kodlaması bütün üstveriyi düşürür. Bu iki yönlü bir şanstır:

   - **GPS sızmaz.** Çekim yerinin koordinatları herkese açık türevde asla
     bulunmaz; silmemiz gerekmiyor, zaten hiç girmiyor. Orijinaller EXIF'iyle
     birlikte özel kovada kalır.
   - **Telif de düşer.** Oysa DMCA bildirimini ayakta tutan şey, dosyanın
     içine gömülü hak bildirimidir. Gömülü telifi kaldırmak birçok ülkede
     ayrıca ihlaldir (WIPO 12. madde, ABD'de DMCA §1202) — yani bu blok
     hırsızlığı önlemez ama sonrasında elde tutulacak tek somut delildir.

   Bu yüzden burada yapılan iş "üstveri temizleme" değil, **geri yazma**dır:
   yalnızca hak alanları, başka hiçbir şey.

   İki biçim iki yol ister. JPEG'de blok bir bölüt olarak araya girer
   (APP13/IPTC ve APP1/XMP). WebP'de dosyanın kendisi genişletilmiş biçime
   (VP8X) sarılmalıdır, çünkü basit WebP'nin üstveri taşıyacak yeri yoktur. */

import { IPTC_RIGHTS } from '@frawgraphy/shared/media';

export type Rights = {
  creator: string;
  copyrightNotice: string;
  usageTerms: string;
  webStatement: string;
  /** Karenin adı — IPTC Object Name olarak yazılır. */
  title?: string | undefined;
};

/** Paylaşılan hak sabitlerinden bir bildirim kurar. */
export function rightsFor(year: number, title?: string): Rights {
  return {
    creator: IPTC_RIGHTS.creator,
    copyrightNotice: IPTC_RIGHTS.copyrightNotice(year),
    usageTerms: IPTC_RIGHTS.usageTerms,
    webStatement: IPTC_RIGHTS.webStatement,
    title
  };
}

const utf8 = new TextEncoder();

const concat = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
};

/* ── XMP ────────────────────────────────────────────────────
   Modern okuyucuların (Adobe, Google Görseller, çoğu CMS) baktığı yer
   burasıdır. IPTC-IIM eski ama hâlâ yaygın olduğu için ikisi birden
   yazılır; çelişmesinler diye aynı değerlerden üretilirler. */

const xmlEscape = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export function buildXmpPacket(rights: Rights): string {
  const title = rights.title
    ? `\n      <dc:title><rdf:Alt><rdf:li xml:lang="x-default">${xmlEscape(rights.title)}</rdf:li></rdf:Alt></dc:title>`
    : '';

  /* `xmpRights:Marked=True` "bu eser telifli" demektir; boş bırakılması
     "bilinmiyor" anlamına gelir ve toplu kazıyıcılar bunu serbest sayar. */
  return `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about=""
      xmlns:dc="http://purl.org/dc/elements/1.1/"
      xmlns:xmpRights="http://ns.adobe.com/xap/1.0/rights/"
      xmlns:plus="http://ns.useplus.org/ldf/xmp/1.0/">
      <dc:creator><rdf:Seq><rdf:li>${xmlEscape(rights.creator)}</rdf:li></rdf:Seq></dc:creator>
      <dc:rights><rdf:Alt><rdf:li xml:lang="x-default">${xmlEscape(rights.copyrightNotice)}</rdf:li></rdf:Alt></dc:rights>${title}
      <xmpRights:Marked>True</xmpRights:Marked>
      <xmpRights:WebStatement>${xmlEscape(rights.webStatement)}</xmpRights:WebStatement>
      <xmpRights:UsageTerms><rdf:Alt><rdf:li xml:lang="x-default">${xmlEscape(rights.usageTerms)}</rdf:li></rdf:Alt></xmpRights:UsageTerms>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
}

/* ── IPTC-IIM ───────────────────────────────────────────────
   Photoshop kaynak bloğu (8BIM) içinde taşınır; JPEG'de APP13'e girer. */

const IPTC_DATASET = {
  codedCharacterSet: [1, 90],
  objectName: [2, 5],
  byline: [2, 80],
  copyrightNotice: [2, 116],
  contact: [2, 118]
} as const;

function iptcField(record: number, dataset: number, value: string): Uint8Array {
  const bytes = utf8.encode(value);
  if (bytes.length > 0xffff) throw new Error('IPTC alanı çok uzun');
  const head = new Uint8Array(5);
  head[0] = 0x1c; // etiket işaretçisi
  head[1] = record;
  head[2] = dataset;
  head[3] = (bytes.length >> 8) & 0xff;
  head[4] = bytes.length & 0xff;
  return concat(head, bytes);
}

/** IPTC veri kümesi — 8BIM sarmalayıcısı olmadan. */
export function buildIptcData(rights: Rights): Uint8Array {
  const fields: Uint8Array[] = [
    /* 1:90 — bundan sonraki alanların UTF-8 olduğunu söyler (ESC % G).
       Yazılmazsa okuyucular Latin-1 varsayar ve Türkçe karakterler bozulur. */
    iptcField(...IPTC_DATASET.codedCharacterSet, '\x1b%G'),
    iptcField(...IPTC_DATASET.byline, rights.creator),
    iptcField(...IPTC_DATASET.copyrightNotice, rights.copyrightNotice),
    iptcField(...IPTC_DATASET.contact, rights.webStatement)
  ];
  if (rights.title) fields.splice(1, 0, iptcField(...IPTC_DATASET.objectName, rights.title));
  return concat(...fields);
}

/** 8BIM kaynak bloğu: "Photoshop 3.0\0" + 8BIM/0x0404 + IPTC verisi. */
export function buildPhotoshopBlock(rights: Rights): Uint8Array {
  const iptc = buildIptcData(rights);
  const header = utf8.encode('Photoshop 3.0\0');

  const resource = new Uint8Array(12);
  resource.set(utf8.encode('8BIM'), 0);
  resource[4] = 0x04; // kaynak kimliği 0x0404 = IPTC-IIM
  resource[5] = 0x04;
  resource[6] = 0x00; // ad: boş Pascal dizesi, çift sayıya tamamlanmış
  resource[7] = 0x00;
  new DataView(resource.buffer).setUint32(8, iptc.length, false);

  /* Kaynak verisi çift uzunlukta olmalı; tek ise bir bayt dolgu eklenir. */
  const pad = iptc.length % 2 === 1 ? new Uint8Array(1) : new Uint8Array(0);
  return concat(header, resource, iptc, pad);
}

/* ── JPEG ───────────────────────────────────────────────────
   Yapı: SOI, ardından bölütler (işaretçi + 2 baytlık uzunluk + yük),
   sonra SOS ve sıkıştırılmış veri. Hak blokları SOI'den hemen sonraya
   (varsa JFIF/APP0'ın ardına) girer. */

export type JpegSegment = { marker: number; payload: Uint8Array; at: number };

const SOI = 0xd8;
const SOS = 0xda;
const EOI = 0xd9;
const APP0 = 0xe0;
const APP1 = 0xe1;
const APP13 = 0xed;

const XMP_NS = 'http://ns.adobe.com/xap/1.0/\0';

/** Bölütleri sırayla döndürür — testler ve idempotan yazım için. */
export function jpegSegments(bytes: Uint8Array): JpegSegment[] {
  if (bytes[0] !== 0xff || bytes[1] !== SOI) throw new Error('JPEG değil (SOI yok)');
  const out: JpegSegment[] = [];
  let i = 2;

  while (i < bytes.length - 1) {
    if (bytes[i] !== 0xff) break;
    const marker = bytes[i + 1]!;
    if (marker === EOI) break;
    /* SOS'tan sonrası sıkıştırılmış veridir, bölüt taraması orada biter. */
    if (marker === SOS) break;
    const length = (bytes[i + 2]! << 8) | bytes[i + 3]!;
    out.push({ marker, payload: bytes.subarray(i + 4, i + 2 + length), at: i });
    i += 2 + length;
  }
  return out;
}

const startsWith = (bytes: Uint8Array, text: string) => {
  const probe = utf8.encode(text);
  if (bytes.length < probe.length) return false;
  return probe.every((b, k) => bytes[k] === b);
};

const isXmp = (s: JpegSegment) => s.marker === APP1 && startsWith(s.payload, XMP_NS);
const isPhotoshop = (s: JpegSegment) =>
  s.marker === APP13 && startsWith(s.payload, 'Photoshop 3.0\0');

/**
 * Sökülecek bölütler.
 *
 * APP1 tamamen atılır: EXIF de XMP de oradadır ve **çekim yerinin GPS
 * koordinatları herkese açık türevde asla bulunmamalıdır** (plan §5.2).
 * Tuvalden çıkan türevde zaten EXIF yoktur; kural, bu işlev bir gün başka
 * bir kaynağa uygulanırsa sessizce sızdırmasın diye burada duruyor.
 *
 * APP13 kendi IPTC bloğumuzdur, yeniden yazılır — aynı dosyada iki kez
 * çalıştırmak güvenli olsun diye.
 *
 * APP0 (JFIF yoğunluğu) ve APP2 (ICC renk profili) korunur: ikisi de
 * üstveri değil, görüntünün doğru gösterilmesi için gereken bilgi.
 */
const isMetadata = (s: JpegSegment) => s.marker === APP1 || isPhotoshop(s);

function jpegSegment(marker: number, payload: Uint8Array): Uint8Array {
  const length = payload.length + 2;
  if (length > 0xffff) throw new Error('JPEG bölütü 64 KB sınırını aşıyor');
  return concat(new Uint8Array([0xff, marker, (length >> 8) & 0xff, length & 0xff]), payload);
}

/**
 * Hak bloklarını JPEG'e yazar. Aynı dosyada iki kez çalıştırmak güvenlidir:
 * eski üstveri bölütleri önce sökülür.
 */
export function embedRightsJpeg(bytes: Uint8Array, rights: Rights): Uint8Array {
  const segments = jpegSegments(bytes);
  const drop = segments.filter(isMetadata);

  /* Ekleme noktası: SOI'nin ardı, ama APP0 varsa onun arkası (yaygın kural). */
  const first = segments[0];
  let insertAt = 2;
  if (first && first.marker === APP0) insertAt = first.at + 2 + first.payload.length + 2;

  const kept: Uint8Array[] = [];
  let cursor = 0;
  for (const s of drop) {
    const end = s.at + 4 + s.payload.length;
    kept.push(bytes.subarray(cursor, s.at));
    cursor = end;
  }
  kept.push(bytes.subarray(cursor));
  const cleaned = concat(...kept);

  /* Söküm ekleme noktasından önce olduysa nokta kaydı; yeniden hesapla. */
  const cleanedSegments = jpegSegments(cleaned);
  const cleanedFirst = cleanedSegments[0];
  insertAt =
    cleanedFirst && cleanedFirst.marker === APP0
      ? cleanedFirst.at + 4 + cleanedFirst.payload.length
      : 2;

  const blocks = concat(
    jpegSegment(APP1, concat(utf8.encode(XMP_NS), utf8.encode(buildXmpPacket(rights)))),
    jpegSegment(APP13, buildPhotoshopBlock(rights))
  );

  return concat(cleaned.subarray(0, insertAt), blocks, cleaned.subarray(insertAt));
}

/* ── WebP ───────────────────────────────────────────────────
   RIFF kapsayıcısı. Basit bir WebP (VP8 ya da VP8L) üstveri taşıyamaz;
   önce genişletilmiş biçime (VP8X) sarılması, sonra `XMP ` yığını
   eklenmesi ve VP8X bayrağında XMP bitinin kaldırılması gerekir. */

export type RiffChunk = { id: string; payload: Uint8Array };

const ascii = (bytes: Uint8Array, at: number, n: number) =>
  String.fromCharCode(...bytes.subarray(at, at + n));

/** RIFF yığınlarını sırayla döndürür. */
export function riffChunks(bytes: Uint8Array): RiffChunk[] {
  if (ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') {
    throw new Error('WebP değil (RIFF/WEBP başlığı yok)');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out: RiffChunk[] = [];
  let i = 12;
  while (i + 8 <= bytes.length) {
    const id = ascii(bytes, i, 4);
    const size = view.getUint32(i + 4, true);
    out.push({ id, payload: bytes.subarray(i + 8, i + 8 + size) });
    /* Yığınlar çift sınırda başlar; tek uzunlukta olan bir bayt doldurulur. */
    i += 8 + size + (size % 2);
  }
  return out;
}

function riffChunk(id: string, payload: Uint8Array): Uint8Array {
  const head = new Uint8Array(8);
  head.set(utf8.encode(id), 0);
  new DataView(head.buffer).setUint32(4, payload.length, true);
  const pad = payload.length % 2 === 1 ? new Uint8Array(1) : new Uint8Array(0);
  return concat(head, payload, pad);
}

/** VP8X başlığı: bayraklar + tuval boyutu (birer eksik, 24 bit, LE). */
function vp8xChunk(width: number, height: number, flags: number): Uint8Array {
  const payload = new Uint8Array(10);
  payload[0] = flags;
  const w = width - 1;
  const h = height - 1;
  payload[4] = w & 0xff;
  payload[5] = (w >> 8) & 0xff;
  payload[6] = (w >> 16) & 0xff;
  payload[7] = h & 0xff;
  payload[8] = (h >> 8) & 0xff;
  payload[9] = (h >> 16) & 0xff;
  return riffChunk('VP8X', payload);
}

/** VP8X bayrak baytındaki bitler. */
const XMP_FLAG = 0x04;
const EXIF_FLAG = 0x08;
const ALPHA_FLAG = 0x10;

export function embedRightsWebp(
  bytes: Uint8Array,
  rights: Rights,
  size: { width: number; height: number }
): Uint8Array {
  /* JPEG'deki kuralın aynısı: EXIF atılır (GPS sızmasın), XMP yeniden
     yazılır. ICC yığını korunur. */
  const chunks = riffChunks(bytes).filter((c) => c.id !== 'XMP ' && c.id !== 'EXIF');
  const xmp = riffChunk('XMP ', utf8.encode(buildXmpPacket(rights)));

  const existing = chunks.find((c) => c.id === 'VP8X');
  let body: Uint8Array;

  if (existing) {
    /* Zaten genişletilmiş biçim: yalnızca bayrağa XMP bitini ekle. */
    const flags = (((existing.payload[0] ?? 0) & ~EXIF_FLAG) | XMP_FLAG) & 0xff;
    const patched = new Uint8Array(existing.payload);
    patched[0] = flags;
    body = concat(
      riffChunk('VP8X', patched),
      ...chunks.filter((c) => c.id !== 'VP8X').map((c) => riffChunk(c.id, c.payload)),
      xmp
    );
  } else {
    /* Basit biçim: VP8X başlığıyla sar. Alfa yığını varsa bayrağı da kalkar,
       yoksa çözücü saydamlığı yok sayar. */
    const hasAlpha = chunks.some((c) => c.id === 'ALPH');
    const flags = XMP_FLAG | (hasAlpha ? ALPHA_FLAG : 0);
    body = concat(
      vp8xChunk(size.width, size.height, flags),
      ...chunks.map((c) => riffChunk(c.id, c.payload)),
      xmp
    );
  }

  const header = new Uint8Array(12);
  header.set(utf8.encode('RIFF'), 0);
  new DataView(header.buffer).setUint32(4, body.length + 4, true);
  header.set(utf8.encode('WEBP'), 8);
  return concat(header, body);
}

/* ── Ortak giriş ────────────────────────────────────────── */

/**
 * Blob'un biçimine bakıp doğru yolu seçer. Tanımadığı bir tip gelirse
 * dosyayı olduğu gibi döndürür — hak bildirimi yazamamak, türevi bozmaktan
 * iyidir; çağıran taraf uyarıyı günlüğe yazar.
 */
export async function embedRights(
  blob: Blob,
  rights: Rights,
  size: { width: number; height: number }
): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());

  if (blob.type === 'image/jpeg') {
    return new Blob([embedRightsJpeg(bytes, rights) as BlobPart], { type: blob.type });
  }
  if (blob.type === 'image/webp') {
    return new Blob([embedRightsWebp(bytes, rights, size) as BlobPart], { type: blob.type });
  }
  return blob;
}

/** Testler ve denetim için: gömülü XMP paketini geri okur. */
export function readXmp(bytes: Uint8Array, type: 'image/jpeg' | 'image/webp'): string | null {
  const decoder = new TextDecoder();
  if (type === 'image/jpeg') {
    const found = jpegSegments(bytes).find(isXmp);
    return found ? decoder.decode(found.payload.subarray(utf8.encode(XMP_NS).length)) : null;
  }
  const chunk = riffChunks(bytes).find((c) => c.id === 'XMP ');
  return chunk ? decoder.decode(chunk.payload) : null;
}

/** Testler için: gömülü IPTC verisini geri okur. */
export function readIptc(bytes: Uint8Array): Uint8Array | null {
  const found = jpegSegments(bytes).find(isPhotoshop);
  if (!found) return null;
  const header = utf8.encode('Photoshop 3.0\0').length;
  const view = new DataView(found.payload.buffer, found.payload.byteOffset, found.payload.byteLength);
  const size = view.getUint32(header + 8, false);
  return found.payload.subarray(header + 12, header + 12 + size);
}
